import { useEffect, useState } from "react";
import { X, RefreshCw, Smartphone } from "lucide-react";
import { useBranding } from "@/hooks/useBranding";
import { forceFlushAndReload } from "@/lib/forceFlush";

const SEEN_VERSION_KEY = "sv_branding_seen_version";
const DISMISS_KEY = "sv_branding_dismissed_version";

/**
 * BrandingUpdateBanner — pops up the FIRST time a client notices that the
 * admin has bumped the logo version since this device last loaded the app.
 *
 * Two states it can be in:
 *   1. Brand-new device (no SEEN_VERSION_KEY yet) → silently store the
 *      current version and never show. We only want to notify EXISTING
 *      installs that a change happened, not first-time visitors.
 *   2. Returning device with `seen < current` → show the modal explaining
 *      that the in-app logo has updated automatically, BUT the home-screen
 *      icon requires a reinstall (OS-level cache limitation on Android).
 *
 * Once dismissed (X) or "Reinstall now" tapped, we record DISMISS_KEY for
 * that version so the banner doesn't re-appear on every page load.
 */
export default function BrandingUpdateBanner() {
  const { brandingVersion, isCustom } = useBranding();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!brandingVersion || brandingVersion < 1) return;
    let seen = 0;
    try { seen = parseInt(localStorage.getItem(SEEN_VERSION_KEY) || "0", 10) || 0; } catch { /* ignore */ }
    if (seen === 0) {
      // First-time device on this app — silently sync the baseline, never popup.
      try { localStorage.setItem(SEEN_VERSION_KEY, String(brandingVersion)); } catch { /* quota */ }
      return;
    }
    if (brandingVersion <= seen) return;
    let dismissed = 0;
    try { dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10) || 0; } catch { /* ignore */ }
    if (dismissed >= brandingVersion) return;
    setShow(true);
  }, [brandingVersion]);

  const close = () => {
    try { localStorage.setItem(DISMISS_KEY, String(brandingVersion)); } catch { /* quota */ }
    try { localStorage.setItem(SEEN_VERSION_KEY, String(brandingVersion)); } catch { /* quota */ }
    setShow(false);
  };

  const reinstall = () => {
    try { localStorage.setItem(DISMISS_KEY, String(brandingVersion)); } catch { /* quota */ }
    try { localStorage.setItem(SEEN_VERSION_KEY, String(brandingVersion)); } catch { /* quota */ }
    forceFlushAndReload("branding-update");
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 pb-4 sm:pb-0"
      data-testid="branding-update-banner"
      role="dialog"
      aria-modal="true"
      aria-labelledby="branding-update-title"
    >
      <div className="bg-[#FAF9F6] border border-[#F4A100] shadow-2xl max-w-md w-full p-5 sm:p-6 relative animate-in fade-in slide-in-from-bottom-4 duration-300">
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 text-[#4A4A4A] hover:text-[#D62828] p-1"
          data-testid="branding-update-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#D62828] text-white flex items-center justify-center shrink-0">
            <RefreshCw size={20} strokeWidth={1.8} />
          </div>
          <div>
            <div className="tracking-luxe text-[10px] text-[#F4A100] font-semibold">APP UPDATE</div>
            <h2 id="branding-update-title" className="font-serif text-lg text-[#D62828] leading-tight">
              New app logo available
            </h2>
          </div>
        </div>

        <p className="text-sm text-[#3F3F3F] leading-relaxed mb-3">
          {isCustom
            ? "We've updated the Paper Kraft Cards logo. The new logo will show inside the app automatically."
            : "Branding has been refreshed. The new look will show inside the app automatically."}
        </p>

        <div className="bg-[#F8F5EE] border border-[#E8DFC8] p-3 mb-4 text-[12px] text-[#4A4A4A] leading-relaxed flex gap-2.5">
          <Smartphone size={16} className="shrink-0 mt-0.5 text-[#D62828]" />
          <div>
            <strong className="text-[#D62828]">For the home-screen icon</strong> to also update, please <span className="font-semibold">uninstall</span> the old app from your phone and <span className="font-semibold">reinstall</span> it from <span className="font-mono text-[11px]">paperkraftcards.in</span>. (Android caches the home-screen icon until reinstall — this is an Android limitation.)
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={reinstall}
            className="flex-1 bg-[#D62828] text-white px-4 py-3 tracking-luxe text-[12px] hover:bg-[#5A1414] transition-colors flex items-center justify-center gap-2"
            data-testid="branding-update-reinstall"
          >
            <RefreshCw size={14} />
            Refresh now
          </button>
          <button
            type="button"
            onClick={close}
            className="flex-1 border border-[#E5E0D8] text-[#4A4A4A] px-4 py-3 tracking-luxe text-[12px] hover:bg-[#F5F2EA] transition-colors"
            data-testid="branding-update-later"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
}
